import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ItemOptions } from "./Cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clear();
          api.get(`/orders/by-session/${sessionId}`).then((r) => setOrder(r.data));
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <div data-testid="payment-checking"><Loader2 className="mx-auto h-14 w-14 animate-spin text-[var(--pink)]" /><h1 className="mt-6 text-4xl">Confirming your payment...</h1></div>
      )}
      {state === "paid" && (
        <div className="rise" data-testid="payment-paid">
          <CheckCircle2 className="mx-auto h-16 w-16 text-[var(--pink)]" />
          <span className="mt-4 inline-block rounded-full bg-[var(--blue)] px-4 py-1 font-extrabold" data-testid="payment-status-badge">Paid</span>
          <h1 className="mt-4 text-4xl sm:text-5xl">Thank you{order ? `, ${order.first_name}` : ""}!</h1>
          <p className="font-script mt-2 text-xl text-[var(--blue-soft)]">for supporting my small business</p>
          {order && (
            <div className="panel mt-10 space-y-3 p-6 text-left" data-testid="order-summary">
              <p className="font-display text-xl">Order {order.order_number}</p>
              {order.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm"><span className="text-white/75">{i.quantity} × {i.name}<ItemOptions item={i} /></span><span>{money(i.unit_price * i.quantity)}</span></div>
              ))}
              <div className="ribbon-thin rounded-full" />
              {order.shipping && <div className="flex justify-between text-sm" data-testid="order-shipping"><span className="text-white/75">Shipping · {order.shipping.label}</span><span>{money(order.shipping.amount_cents / 100)}</span></div>}
              <div className="flex justify-between font-display text-2xl"><span>Total paid</span><span className="text-[var(--pink)]" data-testid="order-total-paid">{money(order.amount_total ?? order.subtotal)}</span></div>
            </div>
          )}
          <p className="mt-6 text-white/65">We'll reach out about pickup or delivery soon. <Heart className="inline h-4 w-4 fill-[var(--pink)] text-[var(--pink)]" /></p>
          <Link to="/shop" className="btn-candy mt-8" data-testid="payment-success-shop-button">Keep shopping</Link>
        </div>
      )}
      {["failed", "timeout", "error"].includes(state) && (
        <div data-testid="payment-not-confirmed">
          <XCircle className="mx-auto h-14 w-14 text-[var(--pink)]" />
          <h1 className="mt-6 text-4xl">{state === "failed" ? "Payment didn't go through" : "We couldn't confirm your payment yet"}</h1>
          <p className="mt-3 text-white/60">{state === "timeout" ? "Please refresh in a moment. If you were charged, we'll still see your order." : "Your cart is saved, so you can try again."}</p>
          <Link to="/cart" className="btn-royal mt-8" data-testid="payment-back-to-cart-button">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
